import _ from "lodash";
import { call, put, select, takeLatest } from "redux-saga/effects";
import { editCompanyEftPull, setLoading } from "../actions";
import { Auth } from "../utilities/auth";
import axios from "../utilities/axiosWrapper";

// Auth reducer
export const selectAuthReducer = (state: any) => state.authReducer;

/**
 * @desc The axios wrapper returns the error object instead of throwing, so on a
 * non 2xx the status and body sit under `response`.
 * @param {*} jsonResponse
 * @return {*} {{ status: number, body: any }}
 */
const readResponse = (jsonResponse: any): { status: number; body: any } => ({
	status: jsonResponse?.status ?? jsonResponse?.response?.status ?? 0,
	body: jsonResponse?.data ?? jsonResponse?.response?.data ?? {},
});

/**
 * @param {*} body
 * @return {string} the API's message, or a generic fallback
 */
const readErrorMessage = (body: any): string =>
	body?.error?.message ?? "Something went wrong. Please try again.";

/**
 * @desc Fetch whether automatic debits are stopped for a company.
 * @param {*} action
 * @return {*}  {Generator}
 */
export function* fetchCompanyEftPullWorker(action: any): Generator {
	let selectedAuthReducer: any = yield select(selectAuthReducer);
	let apikeys: any = null;
	let jsonResponse: any = null;
	let params: any = {
		method: "get",
		path: `businesses/${action.data}/eft-pull?at=${selectedAuthReducer.accessToken}`,
		data: {},
	};

	try {
		apikeys = yield Auth.getAuthTokens();
		jsonResponse = yield call(axios, params, apikeys);

		const { status, body } = readResponse(jsonResponse);

		if (status >= 400) {
			return yield put(
				setLoading({
					loading: false,
					alertType: "error",
					errorMessage: readErrorMessage(body),
				})
			);
		}

		return yield put(editCompanyEftPull(_.get(body, "data", {})));
	} catch (ex) {
		return yield put(
			setLoading({
				loading: false,
				alertType: "error",
				errorMessage: "Could not read the automatic debit setting.",
			})
		);
	}
}

/**
 * @desc Stop or resume automatic debits for a company.
 * @param {*} action
 * @return {*}  {Generator}
 */
export function* updateCompanyEftPullWorker(action: any): Generator {
	yield put(
		setLoading({ loading: true, errorMessage: "", successMessage: "" })
	);
	let selectedAuthReducer: any = yield select(selectAuthReducer);
	let apikeys: any = null;
	let jsonResponse: any = null;
	let params: any = {
		method: "post",
		path: `businesses/${action.data.businessGuid}/eft-pull?at=${selectedAuthReducer.accessToken}`,
		data: { stopped: action.data.stopped },
	};

	try {
		apikeys = yield Auth.getAuthTokens();
		jsonResponse = yield call(axios, params, apikeys);

		const { status, body } = readResponse(jsonResponse);

		if (status >= 200 && status < 300) {
			yield put(editCompanyEftPull(_.get(body, "data", {})));
			return yield put(
				setLoading({
					loading: false,
					alertType: "success",
					successMessage: action.data.stopped
						? "Automatic debits stopped for this company"
						: "Automatic debits resumed for this company",
				})
			);
		}

		return yield put(
			setLoading({
				loading: false,
				alertType: "error",
				errorMessage: readErrorMessage(body),
			})
		);
	} catch (ex) {
		return yield put(
			setLoading({ loading: false, errorMessage: ex.message })
		);
	}
}

/**
 * @return {*}  {Generator}
 */
function* fetchCompanyEftPullWatcher(): Generator {
	yield takeLatest("FETCH_COMPANY_EFT_PULL", fetchCompanyEftPullWorker);
}

/**
 * @return {*}  {Generator}
 */
function* updateCompanyEftPullWatcher(): Generator {
	yield takeLatest("UPDATE_COMPANY_EFT_PULL", updateCompanyEftPullWorker);
}

export { fetchCompanyEftPullWatcher, updateCompanyEftPullWatcher };
